import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './AdminDashboard.css';
import { Line, Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import axios from '../../api/axios';
import DownloadIcon from '@mui/icons-material/Download';
import axiosInstance from '../../api/axios';
const API_BASE_URL = axiosInstance.defaults.baseURL;

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
);
function AdminDashboard() {
  // WebSocket connection
  useEffect(() => {
    const timeout = setTimeout(() => {
      const socket = new WebSocket(`ws://${window.location.hostname}:5001/ws`);

      socket.onopen = () => {
        console.log('✅ WebSocket connected');
        socket.send('Hello server');
      };

      socket.onmessage = (event) => {
        // console.log('📩 Message from server:', event.data);
      };

      socket.onclose = () => {
        console.log('❌ WebSocket disconnected');
      };
    }, 200); // delay connection

    return () => clearTimeout(timeout);
  }, []);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [materials, setMaterials] = useState([]);
  const [newMaterial, setNewMaterial] = useState({
    title: '',
    description: '',
    image: null,
  });
  const [editingId, setEditingId] = useState(null);
  const navigate = useNavigate();
  const [totals, setTotals] = useState({
    totalAlumni: 0,
    totalJobs: 0,
    totalEvents: 0,
    totalFeedback: 0,
  });

  const [lineData, setLineData] = useState(null);
  const [barData, setBarData] = useState(null);
  const [pieData, setPieData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [hasMoreLogs, setHasMoreLogs] = useState(true);
  const logsPerPage = 12;

  const filteredLogs = logs.filter((log) => {
    const matchesSearch = log.details
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchesFilter = filter ? log.entity === filter : true;
    return matchesSearch && matchesFilter;
  });

  // Function to handle CSV export
  const handleExportCSV = async () => {
    try {
      const res = await axios.get('/api/logs/export', {
        params: {
          entity: filter || '',
          search: search || '',
        },
      });

      const logsToExport = res.data;

      const headers = ['Performed By', 'Entity', 'Action Details', 'Date'];
      const rows = logsToExport.map((log) => [
        log.performedBy,
        log.entity,
        log.details,
        new Date(log.createdAt).toLocaleString(),
      ]);

      const csvContent = [headers, ...rows]
        .map((row) =>
          row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','),
        )
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `system_logs_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to export logs:', err);
      alert('Failed to export logs. See console for details.');
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchLogs = async () => {
      try {
        const res = await axios.get('/api/logs', {
          params: { search, entity: filter, page, limit: logsPerPage },
        });

        if (!isMounted) return;

        const fetchedLogs = res.data.logs;

        setLogs((prevLogs) =>
          page === 1 ? fetchedLogs : [...prevLogs, ...fetchedLogs],
        );

        setHasMoreLogs((prevLogs) =>
          page === 1
            ? fetchedLogs.length < res.data.total
            : prevLogs.length + fetchedLogs.length < res.data.total,
        );
      } catch (err) {
        // console.error('Error fetching logs:', err);
        console.error('Error fetching logs');
      }
    };

    fetchLogs();
    const intervalId = setInterval(fetchLogs, 30000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [search, filter, page]);

  // Fetch chart data
  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const [regRes, jobRes, eventStatsRes] = await Promise.all([
          axios.get('/api/alumni/monthly'),
          axios.get('/api/jobs/monthly'),
          axios.get('/api/events/stats'),
        ]);

        setLineData({
          labels: regRes.data.map((d) =>
            new Date(0, d._id - 1).toLocaleString('default', { month: 'long' }),
          ),
          datasets: [
            {
              label: 'Alumni Registrations',
              data: regRes.data.map((d) => d.count),
              borderColor: '#007bff',
              backgroundColor: 'rgba(0, 123, 255, 0.3)',
              tension: 0.3,
            },
          ],
        });

        setBarData({
          labels: jobRes.data.map((d) =>
            new Date(0, d._id - 1).toLocaleString('default', { month: 'long' }),
          ),
          datasets: [
            {
              label: 'Jobs Posted',
              data: jobRes.data.map((d) => d.count),
              backgroundColor: '#28a745',
            },
          ],
        });

        setPieData({
          labels: ['Published', 'Draft', 'Upcoming', 'Past'],
          datasets: [
            {
              data: [
                eventStatsRes.data.published || 0,
                eventStatsRes.data.draft || 0,
                eventStatsRes.data.upcoming || 0,
                eventStatsRes.data.past || 0,
              ],
              backgroundColor: ['#28a745', '#6c757d', '#007bff', '#dc3545'],
            },
          ],
        });
      } catch (err) {
        console.error('Failed to load chart data', err);
      }
    };

    fetchChartData();
  }, []);

  // Fetch materials
  useEffect(() => {
    axios
      .get('/api/publications')
      .then((res) => setMaterials(res.data))
      .catch(console.error);
  }, []);

  const handleClear = () => {
    setNewMaterial({
      title: '',
      description: '',
      image: null,
    });
    setEditingId(null);
  };

  const handleSubmit = async () => {
    if (!newMaterial.title.trim()) return alert('Title is required.');
    if (!newMaterial.description.trim())
      return alert('Description is required.');
    if (!newMaterial.image) return alert('Image is required.');

    const formData = new FormData();
    formData.append('title', newMaterial.title);
    formData.append('description', newMaterial.description);
    formData.append('image', newMaterial.image);

    try {
      const url = editingId
        ? `/api/publications/${editingId}`
        : `/api/publications`;
      const method = editingId ? 'put' : 'post';

      await axios[method](url, formData);
      const refreshed = await axios.get('/api/publications');
      setMaterials(refreshed.data);
      setNewMaterial({ title: '', description: '', image: null });
      setEditingId(null);
    } catch (err) {
      console.error('Failed to submit publication:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/publications/${id}`);
      setMaterials((prev) => prev.filter((mat) => mat._id !== id));
    } catch (err) {
      console.error('Failed to delete material:', err);
    }
  };

  // Fetch data for Total Alumni, Total Events and Total Jobs
  useEffect(() => {
    const fetchAllTotals = async () => {
      try {
        const [alumniRes, eventsRes] = await Promise.all([
          axios.get('/api/alumni/totals'),
          axios.get('/api/events/stats'),
        ]);

        setTotals((prev) => ({
          ...prev,
          totalAlumni: alumniRes.data.totalAlumni,
          totalJobs: alumniRes.data.totalJobs,
          totalEvents: eventsRes.data.upcoming || 0,
        }));
      } catch (error) {
        console.error('Error fetching totals:', error);
      }
    };

    fetchAllTotals();
  }, []);

  // Fetch data for Total Feedback
  useEffect(() => {
    const fetchFeedbackCount = async () => {
      try {
        const res = await axios.get('/api/feedback/unread');
        setTotals((prev) => ({
          ...prev,
          totalFeedback: res.data.totalUnreadFeedbacks,
        }));
      } catch (error) {
        console.error('Error fetching feedback count:', error);
      }
    };

    fetchFeedbackCount();
  }, []);

  const handleNavigate = (path) => {
    if (path) {
      navigate(path);
    }
  };

  // Filter
  useEffect(() => {
    setPage(1);
  }, [search, filter]);

  return (
    <div className="admin-dashboard-content">
      {/* STATS */}
      <div className="dashboard-stats-container">
        {[
          {
            title: 'UPCOMING EVENTS',
            value: totals.totalEvents,
            path: '/EventPage',
          },
          { title: 'OPEN JOBS', value: totals.totalJobs, path: '/JobPost' },
          {
            title: 'TOTAL ALUMNI REGISTERED',
            value: totals.totalAlumni,
            path: '/AlumniInformation',
          },
          {
            title: 'TOTAL UNREAD FEEDBACKS',
            value: totals.totalFeedback,
            path: '/feedBack',
          },
        ].map((stat, index) => (
          <div className="dashboard-card" key={index}>
            <h3>{stat.value}</h3>
            <p>{stat.title}</p>
            <button onClick={() => handleNavigate(stat.path)}>
              View Details
            </button>
          </div>
        ))}
      </div>

      <div className="activity-feed-container">
        {/* Publication Materials */}
        <div className="publication-section">
          {/* <h3>Publication Materials / Announcements</h3> */}
          <h3>PUBLICATION MATERIAL / ANNOUNCEMENTS</h3>

          {/* Upload Form */}
          <form
            className="publication-form"
            onSubmit={(e) => e.preventDefault()}
          >
            <input
              required
              type="text"
              placeholder="Title"
              value={newMaterial.title}
              onChange={(e) =>
                setNewMaterial({ ...newMaterial, title: e.target.value })
              }
            />
            <textarea
              placeholder="Description"
              value={newMaterial.description}
              onChange={(e) =>
                setNewMaterial({ ...newMaterial, description: e.target.value })
              }
              rows={3}
            />

            {newMaterial.image && (
              <img
                src={
                  newMaterial.image instanceof File
                    ? URL.createObjectURL(newMaterial.image)
                    : `${API_BASE_URL}/${newMaterial.image.filePath}`
                }
                alt="Preview"
                className="publication-preview"
              />
            )}

            <label className="custom-file-upload">
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  setNewMaterial({ ...newMaterial, image: e.target.files[0] })
                }
              />
              Upload Image
            </label>

            <div className="form-buttons">
              <button onClick={handleSubmit} className="btnUpdateAdd">
                {editingId ? 'Update' : 'Add'}
              </button>
              <button type="button" className="btn-clear" onClick={handleClear}>
                Clear
              </button>
            </div>
          </form>

          {/* Search and Sort */}
          <div className="search-sort-controls">
            <input
              type="text"
              placeholder="Search by title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>

          {/* Material List */}
          <div className="publication-list">
            {materials
              .filter((mat) =>
                mat.title.toLowerCase().includes(searchTerm.toLowerCase()),
              )
              .sort((a, b) =>
                sortOrder === 'asc'
                  ? new Date(a.createdAt) - new Date(b.createdAt)
                  : new Date(b.createdAt) - new Date(a.createdAt),
              )
              .map((mat) => (
                <div className="publication-card" key={mat._id}>
                  <img
                    src={`${API_BASE_URL}/${mat.image?.filePath}`}
                    alt={mat.title}
                  />

                  <div className="publication-info">
                    <strong>{mat.title}</strong>
                    <p>{mat.description}</p>
                  </div>
                  <div className="publication-actions">
                    <button
                      className="btnEdit"
                      onClick={() => {
                        setNewMaterial({
                          title: mat.title,
                          description: mat.description,
                          image: { filePath: mat.image?.filePath || '' },
                        });
                        setEditingId(mat._id);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(mat._id)}
                      className="btn-delete"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Trend Charts */}
        <div className="trend-charts">
          <h3>SMU ALUMNI PORTAL TRENDS</h3>
          <div className="chart-row">
            <div className="chart-box">
              {lineData ? (
                <Line data={lineData} />
              ) : (
                <p>Loading Line Chart...</p>
              )}
            </div>

            <div className="chart-box">
              {barData ? <Bar data={barData} /> : <p>Loading Bar Chart...</p>}
            </div>

            <div className="chart-box">
              {pieData ? <Pie data={pieData} /> : <p>Loading Pie Chart...</p>}
            </div>
          </div>
        </div>

        {/* Activity Feed */}
        <div className="activity-feed">
          <h3>SYSTEM ACTIVITY FEED</h3>

          {/* Search and Filter UI */}
          <div className="activity-controls">
            <input
              type="text"
              placeholder="Search logs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="">All</option>
              <option value="Admin">Admin</option>
              <option value="Alumni">Alumni</option>
              <option value="Authentication">Authentication</option>
              <option value="Event">Event</option>
              <option value="Job">Job</option>
            </select>
            <button className="export-btn" onClick={handleExportCSV}>
              <DownloadIcon style={{ marginRight: '6px', fontSize: '18px' }} />
              Logs
            </button>
          </div>

          <ul className="log-list">
            <div className="log-legend">
              <span className="dot admin-dot" /> Admin
              <span className="dot alumni-dot" /> Alumni
              <span className="dot authentication-dot" /> Authentication
              <span className="dot event-dot" /> Event
              <span className="dot job-dot" /> Job
            </div>

            {filteredLogs.map((log, i) => (
              <li key={i}>
                <span
                  className={`dot ${
                    log.entity
                      ? log.entity.toLowerCase() + '-dot'
                      : 'default-dot'
                  }`}
                />

                <div className="log-text">
                  <strong>{log.performedBy}</strong> ({log.entity})<br />
                  {log.details}
                  <br />
                  <small>{new Date(log.createdAt).toLocaleString()}</small>
                </div>
              </li>
            ))}
          </ul>

          {hasMoreLogs && (
            <button
              className="load-more-btn"
              onClick={() => setPage((prev) => prev + 1)}
            >
              Load More
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
